import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useNavigate } from "@tanstack/react-router";
import {
	Bot,
	Building2,
	Check,
	ChevronRight,
	CircleUser,
	History,
	Home,
	LogOut,
	Moon,
	Plug,
	Settings2,
	Sun,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { LobuMark } from "@/components/brand/lobu-mark";
import { useLobuLens } from "@/components/lens/lobu-lens";
import { isWorkspaceEntityType } from "@/components/memory/use-visible-entity-types";
import { useIsNarrow } from "@/components/shell/responsive-app-shell";
import {
	Command,
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
	CommandShortcut,
} from "@/components/ui/command";
import { EntityIcon } from "@/components/ui/entity-icon";
import {
	ActivityWaveIcon,
	ConnectorsIcon,
} from "@/components/ui/lobu-nav-icons";
import { useDebounce } from "@/hooks/use-debounce";
import { useOrgContext } from "@/hooks/use-org-context";
import { parseAppHref } from "@/lib/activity-links";
import { useAgents } from "@/lib/api/agents";
import {
	useConnections,
	useInstallableConnectorCatalog,
} from "@/lib/api/connections";
import { useEntityTypes } from "@/lib/api/entities";
import { useSearchMemory } from "@/lib/api/search-memory";
import { organization } from "@/lib/auth";
import {
	flattenSearchMemoryHits,
	type MemoryHit,
} from "@/lib/command-palette-memory";
import { memoryBrowseTypeSearch } from "@/lib/memory-browse";
import { OWNER_SECTIONS, type OwnerSectionKey } from "@/lib/owner-sections";
import { getStored, setStored } from "@/lib/storage";
import { buildOwnerHref } from "@/lib/subdomain";

function useTheme() {
	const [isDark, setIsDark] = useState(
		() =>
			typeof document !== "undefined" &&
			document.documentElement.classList.contains("dark"),
	);

	const toggle = useCallback(() => {
		const newIsDark = !isDark;
		setIsDark(newIsDark);
		document.documentElement.classList.toggle("dark", newIsDark);
		setStored("theme", newIsDark ? "dark" : "light");
	}, [isDark]);

	useEffect(() => {
		const stored = getStored("theme");
		const prefersDark = window.matchMedia(
			"(prefers-color-scheme: dark)",
		).matches;
		const shouldBeDark = stored === "dark" || (!stored && prefersDark);
		setIsDark(shouldBeDark);
		document.documentElement.classList.toggle("dark", shouldBeDark);
	}, []);

	return { isDark, toggle };
}

function MemoryHitGlyph({ hit }: { hit: MemoryHit }) {
	const className = "mr-2 h-4 w-4 shrink-0";
	if (hit.kind === "entity") {
		return <EntityIcon icon={hit.icon} className={className} />;
	}
	if (hit.kind === "connection") {
		return <ConnectorsIcon className={className} />;
	}
	return <ActivityWaveIcon className={className} />;
}

function MemoryHitTrail({ hit }: { hit: MemoryHit }) {
	return (
		<span className="flex min-w-0 items-center gap-1 text-muted-foreground">
			<span className="shrink-0">{hit.kindLabel}</span>
			<ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
			<span className="shrink-0">{hit.typeLabel}</span>
			<ChevronRight className="h-3 w-3 shrink-0 opacity-50" />
			<span className="truncate text-foreground">{hit.name}</span>
		</span>
	);
}

interface CommandBodyProps {
	search: string;
	onSearchChange: (next: string) => void;
	onClose: () => void;
	inputAtBottom?: boolean;
}

function CommandBody({
	search,
	onSearchChange,
	onClose,
	inputAtBottom = false,
}: CommandBodyProps) {
	const navigate = useNavigate();
	const isNarrow = useIsNarrow();
	const { isDark, toggle: toggleTheme } = useTheme();
	const {
		available: lensAvailable,
		isOpen: lensOpen,
		open: openLens,
	} = useLobuLens();
	const { currentOwner, resolvedOrganizationId, allOrgs } = useOrgContext();
	const canManageWorkspace =
		!!currentOwner && allOrgs.some((org) => org.slug === currentOwner);

	const debouncedSearch = useDebounce(search.trim(), 200);
	const hasQuery = debouncedSearch.length >= 1;
	const memoryEnabled = debouncedSearch.length >= 2;
	const lowerQ = debouncedSearch.toLowerCase();

	const goToOwner = (slug: string) => {
		const target = buildOwnerHref(slug);
		if (target.kind === "cross-host") {
			window.location.assign(target.href);
			return;
		}
		void navigate({ to: target.to as "/" });
	};

	const goToHref = (href: string) => {
		const { pathname, search: nextSearch } = parseAppHref(href);
		void navigate({ to: pathname as "/", search: nextSearch as never });
	};

	// Entity / agent / connection search.
	//
	// Cancellation: when the debounced query changes the queryKey changes too,
	// so React Query stops observing the stale request — but our queryFns do
	// NOT thread `signal` into fetchWithTimeout, so the stale fetch keeps
	// running on the network until it completes or times out. Acceptable here
	// because (a) the queries are debounced 200ms, (b) results are gated on
	// ≥1 / ≥2 chars, and (c) the underlying endpoints are cheap. If this ever
	// becomes a problem, thread `signal` through useAgents/useConnections/
	// useSearchMemory → apiCall → fetchWithTimeout.
	//
	// Org gating: connection list queries require a resolved org UUID (not
	// just a slug). On a `/<owner>/...` URL where the URL org is not the
	// currently-active org, `resolvedOrganizationId` is null and that group
	// silently returns no results. Agents and search_memory are slug-based
	// (URL path / `{ slug: currentOwner }`) so they work in both cases.
	const { data: agents = [] } = useAgents({
		enabled: hasQuery && !!currentOwner,
	});
	const { data: connections = [] } = useConnections(
		hasQuery ? resolvedOrganizationId : null,
	);
	// Connector display names for memory rows, from the same catalog the
	// Automations page names connectors by.
	const { data: connectorCatalog } = useInstallableConnectorCatalog(
		hasQuery ? resolvedOrganizationId : null,
	);
	const { data: entityTypes = [] } = useEntityTypes(
		currentOwner ? { slug: currentOwner } : undefined,
	);
	const { data: memoryResult } = useSearchMemory(
		memoryEnabled ? debouncedSearch : "",
		currentOwner ? { slug: currentOwner } : null,
	);

	const filteredAgents = useMemo(() => {
		if (!hasQuery) return [];
		return agents
			.filter((a) => a.name.toLowerCase().includes(lowerQ))
			.slice(0, 5);
	}, [agents, hasQuery, lowerQ]);

	const memoryHits = useMemo(() => {
		if (!memoryEnabled || !currentOwner) return [];
		const types = new Map(
			entityTypes.map((t) => [t.slug, { name: t.name, icon: t.icon }]),
		);
		const connectorNames = new Map(
			(connectorCatalog ?? []).map((definition) => [
				definition.key,
				definition.name,
			]),
		);
		return flattenSearchMemoryHits(
			memoryResult,
			currentOwner,
			types,
			connectorNames,
		);
	}, [
		memoryEnabled,
		currentOwner,
		entityTypes,
		connectorCatalog,
		memoryResult,
	]);

	// Types are places, not search hits: this is how a type's page is reached
	// by name from anywhere.
	const filteredTypes = useMemo(() => {
		if (!hasQuery || !currentOwner) return [];
		return entityTypes
			.filter(
				(t) =>
					isWorkspaceEntityType(t, currentOwner) &&
					`${t.name} ${t.slug}`.toLowerCase().includes(lowerQ),
			)
			.slice(0, 5);
	}, [entityTypes, hasQuery, lowerQ, currentOwner]);

	const memoryConnectionIds = useMemo(() => {
		const ids = new Set<string>();
		for (const hit of memoryHits) {
			if (hit.kind === "connection") {
				ids.add(hit.id.replace(/^connection-/, ""));
			}
		}
		return ids;
	}, [memoryHits]);

	const filteredConnections = useMemo(() => {
		if (!hasQuery) return [];
		return connections
			.filter((c) => {
				if (memoryConnectionIds.has(String(c.id))) return false;
				const label = (
					c.display_name ??
					c.connector_name ??
					c.connector_key ??
					""
				).toLowerCase();
				return label.includes(lowerQ);
			})
			.slice(0, 5);
	}, [connections, hasQuery, lowerQ, memoryConnectionIds]);

	const runCommand = useCallback(
		(command: () => void | Promise<void>) => {
			onClose();
			Promise.resolve(command()).catch(console.error);
		},
		[onClose],
	);

	// SEVERE codex#2: keep DOM order input-then-list (cmdk requires the input
	// to come before the list for keyboard semantics + autofocus). Visually
	// swap them with flex `order-*` when inputAtBottom is true — the parent
	// <Command> is a `flex flex-col` in the narrow case so the swap takes
	// effect; in the wide CommandDialog the parent isn't a flex column, so
	// `order` is a no-op and the natural DOM order wins.
	const input = (
		<CommandInput
			placeholder="Type a command or search..."
			value={search}
			onValueChange={onSearchChange}
			className={inputAtBottom ? "order-2" : undefined}
		/>
	);

	const list = (
		<CommandList className={inputAtBottom ? "order-1 flex-1" : undefined}>
			<CommandEmpty>No results found.</CommandEmpty>

			<CommandGroup heading="Navigation">
				{currentOwner ? (
					(Object.keys(OWNER_SECTIONS) as OwnerSectionKey[]).map((key) => {
						const section = OWNER_SECTIONS[key];
						const Icon = section.icon;
						const keywords =
							key === "context"
								? "memory entities data"
								: key === "connectors"
									? "connectors connections connect"
									: key === "activity"
										? "activity home"
										: section.label.toLowerCase();
						return (
							<CommandItem
								key={key}
								value={keywords}
								onSelect={() =>
									runCommand(() =>
										navigate({
											to: section.to,
											params: { owner: currentOwner },
											search: section.search as never,
										}),
									)
								}
							>
								<Icon className="mr-2 h-4 w-4 shrink-0" />
								<span>{section.label}</span>
								{key === "activity" ? (
									<CommandShortcut>⌘H</CommandShortcut>
								) : null}
							</CommandItem>
						);
					})
				) : (
					<CommandItem
						value="home"
						onSelect={() => runCommand(() => navigate({ to: "/" }))}
					>
						<Home className="mr-2 h-4 w-4" />
						<span>Home</span>
						<CommandShortcut>⌘H</CommandShortcut>
					</CommandItem>
				)}
				{/* Wide layouts list Recent in the sidebar; narrow ones keep it
				    behind Home's Sessions switch. */}
				{currentOwner && isNarrow ? (
					<CommandItem
						value="recent"
						onSelect={() =>
							runCommand(() =>
								navigate({
									to: "/$owner",
									params: { owner: currentOwner },
									search: { tab: "sessions" },
								}),
							)
						}
					>
						<History className="mr-2 h-4 w-4 shrink-0" />
						<span>Recent</span>
					</CommandItem>
				) : null}
				{lensAvailable ? (
					<CommandItem
						value="lobu lens overlay"
						onSelect={() => runCommand(() => openLens())}
					>
						<LobuMark className="mr-2 h-4 w-4 shrink-0" />
						<span>{lensOpen ? "Lobu Lens is open" : "Open Lobu Lens"}</span>
					</CommandItem>
				) : null}
			</CommandGroup>

			{filteredTypes.length > 0 && (
				<>
					<CommandSeparator />
					<CommandGroup heading="Types">
						{filteredTypes.map((t) => (
							<CommandItem
								key={`type-${t.slug}`}
								value={`type ${t.name} ${t.slug}`}
								onSelect={() =>
									runCommand(() => {
										if (!currentOwner) return;
										navigate(memoryBrowseTypeSearch(currentOwner, t.slug));
									})
								}
							>
								<EntityIcon
									icon={t.icon}
									className="mr-2 h-4 w-4 shrink-0 text-muted-foreground"
								/>
								<span className="flex-1 truncate">{t.name || t.slug}</span>
								{t.entity_count != null ? (
									<span className="ml-2 shrink-0 text-xs tabular-nums text-muted-foreground">
										{t.entity_count.toLocaleString()} records
									</span>
								) : null}
							</CommandItem>
						))}
					</CommandGroup>
				</>
			)}

			{filteredAgents.length > 0 && (
				<>
					<CommandSeparator />
					<CommandGroup heading="Agents">
						{filteredAgents.map((a) => (
							<CommandItem
								key={`agent-${a.agentId}`}
								value={`agent ${a.name} ${a.agentId}`}
								onSelect={() =>
									runCommand(() => {
										if (!currentOwner) return;
										navigate({
											to: "/$owner/agents/$agentId/settings",
											params: { owner: currentOwner, agentId: a.agentId },
											search: { run_id: undefined },
										});
									})
								}
							>
								<Bot className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
								<span className="flex-1 truncate">{a.name}</span>
								<span className="ml-2 text-xs text-muted-foreground shrink-0">
									in Agents
								</span>
							</CommandItem>
						))}
					</CommandGroup>
				</>
			)}

			{filteredConnections.length > 0 && (
				<>
					<CommandSeparator />
					<CommandGroup heading="Connectors">
						{filteredConnections.map((c) => {
							const label =
								c.display_name ?? c.connector_name ?? c.connector_key;
							return (
								<CommandItem
									key={`conn-${c.id}`}
									value={`connector ${label} ${c.connector_key} ${c.id}`}
									onSelect={() =>
										runCommand(() => {
											if (!currentOwner) return;
											navigate({
												to: "/$owner/connectors/$connectorKey/$connectionId",
												params: {
													owner: currentOwner,
													connectorKey: c.connector_key,
													connectionId: String(c.id),
												},
											});
										})
									}
								>
									<Plug className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
									<span className="flex-1 truncate">{label}</span>
									<span className="ml-2 text-xs text-muted-foreground shrink-0">
										in Connectors
									</span>
								</CommandItem>
							);
						})}
					</CommandGroup>
				</>
			)}

			{memoryHits.length > 0 && (
				<>
					<CommandSeparator />
					{/* Entity + event hits: the Memory hub's own content, so the
					    heading follows the nav label. */}
					<CommandGroup heading="Memory">
						{memoryHits.map((hit) => (
							<CommandItem
								key={hit.id}
								value={`${search} ${hit.kind} ${hit.typeLabel} ${hit.name} ${hit.id}`}
								onSelect={() => runCommand(() => goToHref(hit.href))}
							>
								<MemoryHitGlyph hit={hit} />
								<MemoryHitTrail hit={hit} />
							</CommandItem>
						))}
					</CommandGroup>
				</>
			)}

			{allOrgs.length > 0 ? (
				<>
					<CommandSeparator />
					<CommandGroup heading="Workspaces">
						{allOrgs.map((org) => (
							<CommandItem
								key={org.id}
								value={`workspace ${org.name} ${org.slug} switch org organization`}
								onSelect={() =>
									runCommand(async () => {
										if (org.slug === currentOwner) return;
										await organization.setActive({ organizationId: org.id });
										goToOwner(org.slug);
									})
								}
							>
								<Building2 className="mr-2 h-4 w-4 shrink-0" />
								<span className="flex-1 truncate">{org.name}</span>
								{org.slug === currentOwner ? (
									<Check className="ml-2 h-4 w-4 shrink-0 text-primary" />
								) : null}
							</CommandItem>
						))}
					</CommandGroup>
				</>
			) : null}

			<CommandSeparator />

			<CommandGroup heading="Settings">
				{currentOwner && canManageWorkspace ? (
					<CommandItem
						value="workspace settings organization"
						onSelect={() =>
							runCommand(() =>
								navigate({
									to: "/$owner/settings/organization",
									params: { owner: currentOwner },
								}),
							)
						}
					>
						<Settings2 className="mr-2 h-4 w-4 shrink-0" />
						<span>Workspace settings</span>
					</CommandItem>
				) : null}
				<CommandItem
					value="account settings personal"
					onSelect={() =>
						runCommand(() => navigate({ to: "/account/settings" }))
					}
				>
					<CircleUser className="mr-2 h-4 w-4 shrink-0" />
					<span>Account settings</span>
				</CommandItem>
			</CommandGroup>

			<CommandSeparator />

			<CommandGroup heading="Actions">
				<CommandItem
					value="toggle theme dark light mode"
					onSelect={() => runCommand(toggleTheme)}
				>
					{isDark ? (
						<Sun className="mr-2 h-4 w-4" />
					) : (
						<Moon className="mr-2 h-4 w-4" />
					)}
					<span>{isDark ? "Light Mode" : "Dark Mode"}</span>
					<CommandShortcut>⌘D</CommandShortcut>
				</CommandItem>
				<CommandItem
					value="sign out"
					onSelect={() => runCommand(() => navigate({ to: "/auth/logout" }))}
				>
					<LogOut className="mr-2 h-4 w-4" />
					<span>Sign Out</span>
				</CommandItem>
			</CommandGroup>
		</CommandList>
	);

	// Always render <CommandInput> before <CommandList> in DOM (cmdk relies
	// on this for keyboard semantics, autofocus, and active-descendant).
	// Visual placement at the bottom on narrow is done with `order-1` on
	// the list and `order-2` on the input (parent is `flex flex-col` in
	// the narrow overlay; on the centered wide CommandDialog the parent
	// isn't a flex column so `order` is a no-op and DOM order wins).
	return (
		<>
			{input}
			{list}
		</>
	);
}

export function CommandPalette() {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const isNarrow = useIsNarrow();

	useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setOpen((o) => !o);
			}
		};

		document.addEventListener("keydown", down);
		return () => document.removeEventListener("keydown", down);
	}, []);

	useEffect(() => {
		if (!open) setSearch("");
	}, [open]);

	const close = useCallback(() => setOpen(false), []);

	if (isNarrow) {
		// SEVERE codex#1: use Radix Dialog primitives so we get a real focus
		// trap, Escape handling, scroll-lock, and opener-focus-restore — the
		// previous plain <div role="dialog"> had none of those.
		return (
			<DialogPrimitive.Root open={open} onOpenChange={setOpen}>
				<DialogPrimitive.Portal>
					<DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-background data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
					<DialogPrimitive.Content
						className="fixed inset-0 z-[60] flex flex-col bg-background outline-none"
						aria-describedby={undefined}
						style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
						onOpenAutoFocus={(event) => {
							// Forward focus to cmdk's <CommandInput> so the keyboard
							// pops immediately on open. Escape still closes via Radix.
							event.preventDefault();
							const input =
								document.querySelector<HTMLInputElement>("[cmdk-input]");
							input?.focus();
						}}
					>
						<DialogPrimitive.Title className="sr-only">
							Search
						</DialogPrimitive.Title>
						<Command className="flex flex-1 flex-col">
							<CommandBody
								search={search}
								onSearchChange={setSearch}
								onClose={close}
								inputAtBottom
							/>
						</Command>
					</DialogPrimitive.Content>
				</DialogPrimitive.Portal>
			</DialogPrimitive.Root>
		);
	}

	return (
		<CommandDialog open={open} onOpenChange={setOpen}>
			<CommandBody search={search} onSearchChange={setSearch} onClose={close} />
		</CommandDialog>
	);
}
